<template>
  <div class="cert-card">
    <!-- Header -->
    <div class="cert-card__header">
      <div class="cert-card__header-left">
        <i class="uil-medal cert-card__icon"></i>
        <span class="cert-card__name">{{ certification.certificate?.name }}</span>
      </div>
      <span class="cert-card__id">#{{ certification?.unique_certificate_id }}</span>
    </div>

    <!-- Body: info + actions yan yana (web), alt alta (mobil) -->
    <div class="cert-card__body">
      <div class="cert-card__info-grid">
        <div class="cert-card__info-item">
          <span class="cert-card__label">{{ $t('general.assignment_name') }}</span>
          <span class="cert-card__value">{{ certification.assignmentable?.name || '---' }}</span>
        </div>
        <div class="cert-card__info-item">
          <span class="cert-card__label">{{ $t('certificate.content_title') }}</span>
          <span class="cert-card__value">{{ certification.training_name || certification.certificate?.name }}</span>
        </div>
        <div class="cert-card__info-item">
          <span class="cert-card__label">{{ $t('certification.get_date') }}</span>
          <span class="cert-card__value">{{ formattedCreatedAt }}</span>
        </div>
        <div class="cert-card__info-item">
          <span class="cert-card__label">{{ $t('certification.end_date') }}</span>
          <span class="cert-card__value">{{ formattedEndDate }}</span>
        </div>
      </div>

      <!-- Actions: web'de sağda dikey, mobilde altta yatay -->
      <div class="cert-card__actions">
        <button class="cert-card__btn cert-card__btn--preview" @click="$emit('preview', certification)">
          <i class="uil-eye"></i>
          {{ $t('certification.preview') }}
        </button>
        <button
          v-show="false"
          :class="{ 'cert-card__btn--loading': isSharing }"
          :disabled="isSharing || !certification.assignmentable"
          class="cert-card__btn cert-card__btn--linkedin"
          @click="$emit('share', certification)"
        >
          <i class="fab fa-linkedin"></i>
          {{ isSharing ? $t('general.preparing') : $t('certification.linked') }}
        </button>
        <button
          :disabled="isDownloading"
          class="cert-card__btn cert-card__btn--download"
          type="button"
          @click="$emit('download', certification)"
        >
          <i :class="isDownloading ? 'uil-spinner-alt cert-card__spin' : 'uil-file-download'"></i>
          {{ isDownloading ? ($t('general.downloading') || 'İndiriliyor...') : $t('certification.pdf') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/az';
import 'moment/locale/tr';
import 'moment/locale/en-gb';

export default {
  name: "CertificateCard",
  props: {
    certification: {
      type: Object,
      required: true
    },
    isSharing: {
      type: Boolean,
      default: false
    },
    isDownloading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    formattedCreatedAt() {
      if (!this.certification.created_at) return '---';
      return moment(this.certification.created_at)
        .locale(this.timeZone())
        .format('DD MMMM YYYY');
    },
    formattedEndDate() {
      if (this.certification.certificate?.expireable === 0) {
        return this.$t('certificate.indefinite');
      }
      if (!this.certification.certificate?.end_date) return '---';
      return moment(this.certification.certificate?.end_date)
        .locale(this.timeZone())
        .format('DD MMMM YYYY');
    }
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════
   BASE — Web varsayılan
   ═══════════════════════════════════════ */
.cert-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 12px;
  overflow: hidden;
}

/* ── Header ── */
.cert-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #eef0f3;
  gap: 8px;
}

.cert-card__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.cert-card__icon {
  font-size: 18px;
  color: #f5a623;
  flex-shrink: 0;
}

.cert-card__name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cert-card__id {
  font-size: 12px;
  color: #999;
  background: #f5f5f5;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Body ── */
.cert-card__body {
  padding: 14px 20px;
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.cert-card__info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.cert-card__info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.cert-card__label {
  font-size: 12px;
  font-weight: 500;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.cert-card__value {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

/* ── Actions ── */
.cert-card__actions {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 150px;
  gap: 6px;
}

.cert-card__btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.cert-card__btn:active {
  transform: scale(0.98);
  opacity: 0.85;
}

.cert-card__btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.cert-card__btn--preview {
  background: var(--infoColor);
  color: #fff;
}

.cert-card__btn--download {
  background: var(--successColor);
  color: #fff;
}

.cert-card__btn--linkedin {
  background: #0077b5;
  color: #fff;
}

.cert-card__btn--loading {
  cursor: not-allowed;
  opacity: 0.7;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.cert-card__spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}

/* ═══════════════════════════════════════
   MOBİL (≤768px) — touch-friendly
   ═══════════════════════════════════════ */
@media (max-width: 768px) {
  .cert-card {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .cert-card__header {
    padding: 16px;
  }

  .cert-card__icon {
    font-size: 20px;
  }

  .cert-card__name {
    font-size: 16px;
  }

  .cert-card__body {
    padding: 16px;
    flex-direction: column;
    gap: 16px;
  }

  .cert-card__info-grid {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .cert-card__info-item {
    gap: 4px;
  }

  .cert-card__actions {
    flex-direction: row;
    width: 100%;
  }

  .cert-card__btn {
    flex: 1;
    width: auto;
    height: 48px;
    font-size: 14px;
    border-radius: 10px;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .cert-card {
    border-radius: 12px;
    margin-bottom: 12px;
  }

  .cert-card__header,
  .cert-card__body {
    padding-left: 12px;
    padding-right: 12px;
  }

  .cert-card__header {
    align-items: flex-start;
  }

  .cert-card__name {
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.4;
  }

  .cert-card__body {
    gap: 12px;
  }

  .cert-card__info-grid {
    gap: 12px 8px;
  }

  .cert-card__label {
    font-size: 11px;
    line-height: 1.3;
  }

  .cert-card__value {
    font-size: 14px;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  .cert-card__actions {
    gap: 8px;
  }

  .cert-card__btn {
    min-width: 0;
    padding: 10px 8px;
    font-size: 13px;
  }
}
</style>
